/**
 * URL domain helper.
 *
 * extractDomain parses a URL and returns just the registrable domain label,
 * lowercased and stripped to alphanumerics, with the TLD removed (handles
 * two-part TLDs like co.uk / com.au via TWO_PART_TLDS) and any www. or other
 * subdomain dropped; throws on unparseable input. Used by components/ItemLink
 * and the admin TrainingDataCreateDisplay page; covered by
 * __tests__/submission.test.ts.
 */
// Common two-part TLDs (e.g., co.uk, com.au)
const TWO_PART_TLDS = new Set([
    "co.uk",
    "co.jp",
    "co.in",
    "co.za",
    "co.kr",
    "co.id",
    "co.nz",
    "co.il",
    "com.au",
    "com.br",
    "com.mx",
    "com.ar",
    "com.co",
    "com.sg",
    "com.tw",
    "ac.uk",
    "gov.uk",
    "org.uk",
    "net.uk",
]);

/**
 * Extract the domain from a URL, strip TLD, and return safe alphanumeric-only version.
 * Handles single TLDs (.com, .org, .net, etc.) and two-part TLDs (.co.uk, .com.au, etc.).
 *
 * @param url The URL to extract the domain from
 * @returns The safe domain name with only alphanumeric characters, no TLD
 * @throws Error if the URL is invalid or cannot be parsed
 *
 * @example
 * extractDomain("https://www.example.com/product/123") // returns "example"
 * extractDomain("http://sub.example.org") // returns "example"
 * extractDomain("https://ebay.co.uk/item/123") // returns "ebay"
 * extractDomain("https://shop.amazon.com.au/product") // returns "amazon"
 * extractDomain("https://example-site.com") // returns "examplesite"
 */
export function extractDomain(url: string): string {
    if (!url || typeof url !== "string") {
        throw new Error(`Invalid URL: ${url}`);
    }

    // Add protocol if missing so URL constructor works correctly
    const urlToParse =
        url.startsWith("http://") || url.startsWith("https://") ? url : `http://${url}`;

    let hostname: string;
    try {
        const urlObject = new URL(urlToParse);
        hostname = urlObject.hostname;
    } catch (error) {
        throw new Error(`Could not parse URL: ${url}`);
    }

    if (!hostname) {
        throw new Error(`Could not extract hostname from URL: ${url}`);
    }

    // Validate hostname doesn't contain invalid characters
    if (hostname.includes(" ") || !/^[a-z0-9\.\-]+$/i.test(hostname)) {
        throw new Error(`Invalid hostname in URL: ${url}`);
    }

    // Remove www. prefix if present
    hostname = hostname.replace(/^www\./, "").toLowerCase();

    // Handle single-part hostnames (no dots)
    if (!hostname.includes(".")) {
        return hostname.replace(/[^a-z0-9]/g, "");
    }

    // Extract domain label (strip TLD)
    const parts = hostname.split(".");
    let domainLabel = "";

    if (parts.length < 2) {
        domainLabel = hostname;
    } else if (parts.length >= 3) {
        // Check for two-part TLD
        const lastTwo = `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
        if (TWO_PART_TLDS.has(lastTwo)) {
            domainLabel = parts[parts.length - 3];
        } else {
            domainLabel = parts[parts.length - 2];
        }
    } else {
        // parts.length === 2
        const lastTwo = `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
        if (TWO_PART_TLDS.has(lastTwo)) {
            domainLabel = "";
        } else {
            domainLabel = parts[parts.length - 2];
        }
    }

    // Return safe version (alphanumeric only)
    return domainLabel.toLowerCase().replace(/[^a-z0-9]/g, "");
}
